"use client";

import { usePathname } from "next/navigation";
import { useNotificationBell } from "@/hooks/useNotificationBell";
import { useDevMode, useIsPWA } from "@/contexts/dev-mode-context";

import type {
	HeaderBellVariant,
	HeaderMilestoneAdStudioVariant,
	HeaderProfileVariant,
} from "./types";

import NavBarFramerComponent from "@/framer/environment-v2/nav-bar";

export interface HeaderNavigationBarProps {
	profileVariant?: HeaderProfileVariant;
	bellVariant?: HeaderBellVariant;
	milestoneAdStudioVariant?: HeaderMilestoneAdStudioVariant;
	statusBarPlaceholder?: boolean;
}

function resolveProfileVariant(
	pathname: string,
	explicit?: HeaderProfileVariant,
): HeaderProfileVariant {
	if (explicit) return explicit;
	return pathname.startsWith("/profilesettings") ? "Variant 2" : "Variant 1";
}

function resolveMilestoneVariant(
	pathname: string,
	explicit?: HeaderMilestoneAdStudioVariant,
): HeaderMilestoneAdStudioVariant {
	if (explicit) return explicit;
	return pathname.startsWith("/milestones") ? "Variant 2" : "Variant 1";
}

export function HeaderNavigationBar({
	profileVariant,
	bellVariant: explicitBellVariant,
	milestoneAdStudioVariant,
	statusBarPlaceholder: explicitStatusBar,
}: HeaderNavigationBarProps) {
	const pathname = usePathname() ?? "";
	const { bellVariant: dynamicBellVariant } = useNotificationBell();
	const { isPhoneMockupMode } = useDevMode();
	const isPWA = useIsPWA();
	const resolvedBellVariant = explicitBellVariant ?? dynamicBellVariant;
	const resolvedStatusBar = explicitStatusBar ?? (!isPWA && isPhoneMockupMode);

	return (
		<div className="w-full max-w-[393px] overflow-x-clip [&>*]:!max-w-full" data-testid="shared-shell-header">
			<NavBarFramerComponent.Responsive
				bellVariant={resolvedBellVariant}
				profileVariant={resolveProfileVariant(pathname, profileVariant)}
				milestoneAdStudio={resolveMilestoneVariant(pathname, milestoneAdStudioVariant)}
				statusBarPlaceholder={resolvedStatusBar}
				style={{ width: "100%" }}
				width="100%"
			/>
		</div>
	);
}
